import {
  CheckboxList,
  CheckboxListItem,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo } from "react";
import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useSelector } from "react-redux";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { CheckboxField } from "../../data-model/pharmaPaFormModel";
import { ScreenNamesForBigQuery } from "../../../shared/enums/eventsData";
import { EventsName } from "../../../shared/enums/eventsName";
import { useResolvedFieldOptions } from "../../hooks/useResolvedFieldOptions";
import { checkIfErrorExists } from "../../pages/pharmaPaForm/utils/checkIfErrorExists";
import { RootState } from "../../redux/store/store";
import { getNestedValue } from "../../utils/getNestedValue";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";

const EMPTY_ARRAY: string[] = [];

interface CheckboxFieldProps {
  field: CheckboxField;
  fieldDetails?: Record<string, any>;
  data?: any;
  originalData?: any;
  shouldEmitLogEvent?: boolean;
}

const RenderCheckboxField = (props: CheckboxFieldProps) => {
  const { formFieldsData, setFormFieldsData } = usePharmaFormFields();
  const { resolvedOptions } = useResolvedFieldOptions(props.field, EMPTY_ARRAY);

  const { user } = useAuthSelector((state) => state.firebaseAuthentication,
  );
  const { data: singleMedicalPaFormConfig } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const initialDataValue = getNestedValue(props?.data, props.field.key) ?? [];

  const getSelectedValues = useCallback((): string[] => {
    if (props?.field?.readOnly) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? [];
    }
    if (formFieldsData?.[props?.field?.key]?.isFieldDirty) {
      return formFieldsData?.[props?.field?.key]?.filledValue ?? [];
    }
    if (
      initialDataValue &&
      Array.isArray(initialDataValue) &&
      initialDataValue.length > 0
    ) {
      return initialDataValue;
    }
    return props?.field?.default ?? [];
  }, [formFieldsData, props.field, initialDataValue]);

  const checkboxItems: CheckboxListItem[] = useMemo(() => {
    const selected = getSelectedValues();
    return (resolvedOptions ?? []).map((option) => ({
      id: option.value,
      label: option.label,
      checked: selected.includes(option.value),
      disabled: props?.field?.readOnly,
    }));
  }, [resolvedOptions, getSelectedValues, props?.field?.readOnly]);

  const handleSelectionChange = (
    items: CheckboxListItem[],
    _changedId: string,
  ) => {
    const newValues = items
      .filter((item) => item.checked)
      .map((item) => item.id);

    const originalDataValue =
      getNestedValue(props?.originalData, props.field.key) ?? [];

    setFormFieldsData((prev) => ({
      ...prev,
      [props?.field?.key]: {
        ...prev[props?.field?.key],
        filledValue: newValues,
        isFieldDirty:
          JSON.stringify(originalDataValue) !== JSON.stringify(newValues),
      },
    }));

    if (props?.shouldEmitLogEvent) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: props?.originalData?.patient_id,
        order_id: props?.originalData?.order_id,
        org_id: getOrgIdForFetchExternalWorklist(),
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          EventsName.FORM_FIELD_EDITED + " (" + props?.field?.label + ")",
        additional_data: {
          field_key: props?.field?.key,
          new_value: newValues,
          old_value: getNestedValue(props?.originalData, props.field.key) ?? [],
          screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
          form_id: singleMedicalPaFormConfig?.id ?? "",
          field_name: props?.field?.label ?? "",
          payer_name: payerName,
        },
      });
    }
  };

  return (
    <div
      key={props?.field?.key}
      className="checkbox__container flex-1"
      style={{
        maxWidth: props?.field?.width ? `${props.field.width}%` : "100%",
      }}
    >
      <div className="checkbox__label mb-1 flex gap-1 text-h12 font-semibold">
        {props?.field?.label}
        {props?.field?.isRequired && (
          <div className="checkbox__label--required text-tertiaryRed-5">*</div>
        )}
      </div>
      <CheckboxList
        items={checkboxItems}
        onSelectionChange={handleSelectionChange}
        ariaLabel={props?.field?.label}
        className="!p-0"
      />
      <div className="checkbox__error text-tiny font-normal text-[#cd0202]">
        {checkIfErrorExists(
          formFieldsData,
          props.field.key,
          props?.field?.readOnly,
        )
          ? `${props.field.validationMessage ?? "This field is required"}`
          : ""}
      </div>
      {(props?.field?.additionalInfoHeader ||
        props?.field?.additionalInfoContent) && (
        <div className="section-additonal-info flex w-full flex-col gap-2 bg-secondaryYellow-11 p-3">
          <div className="section-additonal-info__header text-tiny font-bold">
            {props?.field?.additionalInfoHeader ?? ""}
          </div>
          <div className="section-additonal-info__content text-[0.8125rem] font-normal">
            {props?.field?.additionalInfoContent ?? ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderCheckboxField;
